import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { adminApi } from "@/api/admin";
import type { LookupItem, Regola, RegolaInput } from "@/types/admin";
import { tierForTargeting, tierLabel, tierScope } from "./tier";
import { Autocomplete } from "./Autocomplete";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  regola?: Regola | null;
  onSaved: () => void;
}

// --- main form ---
export function RegolaForm({ open, onOpenChange, regola, onSaved }: Props) {
  const isEdit = !!regola;

  const [agenzia, setAgenzia] = useState<LookupItem | null>(null);
  const [offerta, setOfferta] = useState<LookupItem | null>(null);
  const [fornitore, setFornitore] = useState<string>("");
  const [fornitori, setFornitori] = useState<string[]>([]);
  const [valore, setValore] = useState<string>("");
  const [metodoPagamento, setMetodoPagamento] = useState<string>("");
  const [valoreRicavo, setValoreRicavo] = useState<string>("");
  const [nome, setNome] = useState<string>("");
  const [valoreError, setValoreError] = useState<string | null>(null);
  const [valoreRicavoError, setValoreRicavoError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: "ok" | "err" } | null>(null);

  function showToast(msg: string, type: "ok" | "err") {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  // Load fornitori on open
  useEffect(() => {
    if (!open) return;
    adminApi.lookupFornitori().then(setFornitori).catch(() => {});
  }, [open]);

  // Prefill on edit
  useEffect(() => {
    if (!open) return;
    if (regola) {
      setAgenzia(
        regola.agenzia_id && regola.agenzia_name
          ? { id: regola.agenzia_id, name: regola.agenzia_name }
          : null
      );
      setOfferta(
        regola.offerta_id && regola.offerta_name
          ? { id: regola.offerta_id, name: regola.offerta_name }
          : null
      );
      setFornitore(regola.fornitore ?? "");
      setValore(regola.valore != null ? String(regola.valore) : "");
      setMetodoPagamento(regola.metodo_pagamento ?? "");
      setValoreRicavo(regola.valore_ricavo != null ? String(regola.valore_ricavo) : "");
      setNome(regola.name ?? "");
    } else {
      setAgenzia(null);
      setOfferta(null);
      setFornitore("");
      setValore("");
      setMetodoPagamento("");
      setValoreRicavo("");
      setNome("");
    }
    setValoreError(null);
    setValoreRicavoError(null);
  }, [open, regola]);

  // Il campo "Extra ricavo" ha senso solo sulle righe SDD: tornando a "qualsiasi" si svuota.
  function handleMetodoPagamentoChange(v: string) {
    setMetodoPagamento(v);
    if (v !== "SDD") {
      setValoreRicavo("");
      setValoreRicavoError(null);
    }
  }

  const tier = tierForTargeting(agenzia?.id, offerta?.id, fornitore || null);
  const tierInvalid = tier === null;

  function validateValore(): boolean {
    const n = parseFloat(valore);
    if (valore.trim() === "" || isNaN(n) || n < 0) {
      setValoreError("Inserisci un valore numerico ≥ 0");
      return false;
    }
    setValoreError(null);
    return true;
  }

  // Extra ricavo: opzionale, ma se valorizzato deve essere un numero ≥ 0.
  function validateValoreRicavo(): boolean {
    if (metodoPagamento !== "SDD" || valoreRicavo.trim() === "") {
      setValoreRicavoError(null);
      return true;
    }
    const n = parseFloat(valoreRicavo);
    if (isNaN(n) || n < 0) {
      setValoreRicavoError("Inserisci un valore numerico ≥ 0");
      return false;
    }
    setValoreRicavoError(null);
    return true;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const valoreOk = validateValore();
    const valoreRicavoOk = validateValoreRicavo();
    if (!valoreOk || !valoreRicavoOk || tierInvalid) return;

    const body: RegolaInput = {
      agenzia_id: agenzia?.id ?? null,
      offerta_id: offerta?.id ?? null,
      fornitore: fornitore || null,
      valore: parseFloat(valore),
      name: nome.trim() || null,
      metodo_pagamento: metodoPagamento || null,
      valore_ricavo:
        metodoPagamento === "SDD" && valoreRicavo.trim() !== ""
          ? parseFloat(valoreRicavo)
          : null,
    };

    setSubmitting(true);
    try {
      if (isEdit && regola) {
        await adminApi.updateRegola(regola.id, body);
      } else {
        await adminApi.createRegola(body);
      }
      showToast(isEdit ? "Regola aggiornata" : "Regola creata", "ok");
      setTimeout(() => {
        onSaved();
        onOpenChange(false);
      }, 600);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Errore sconosciuto";
      showToast(`Errore: ${msg}`, "err");
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="bg-card text-foreground rounded-xl max-w-lg w-full p-6 shadow-xl border border-border max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold">
            {isEdit ? "Modifica regola" : "Nuova regola"}
          </h3>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Agenzia */}
          <Autocomplete
            id="regola-agenzia"
            label="Agenzia (facoltativo)"
            value={agenzia}
            onChange={setAgenzia}
            fetchItems={(q) => adminApi.lookupAgenzie(q)}
            placeholder="Cerca agenzia..."
          />

          {/* Offerta */}
          <Autocomplete
            id="regola-offerta"
            label="Offerta (facoltativo)"
            value={offerta}
            onChange={setOfferta}
            fetchItems={(q) => adminApi.lookupOfferte(q)}
            placeholder="Cerca offerta..."
          />

          {/* Fornitore */}
          <div className="space-y-1">
            <label htmlFor="regola-fornitore" className="text-sm font-medium text-foreground">
              Fornitore (facoltativo)
            </label>
            <div className="relative">
              <select
                id="regola-fornitore"
                value={fornitore}
                onChange={(e) => setFornitore(e.target.value)}
                className="h-8 w-full rounded-lg border border-input bg-transparent px-2.5 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 appearance-none pr-7"
              >
                <option value="">— nessun fornitore —</option>
                {fornitori.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
                ▾
              </span>
            </div>
          </div>

          {/* Valore */}
          <div className="space-y-1">
            <label htmlFor="regola-valore" className="text-sm font-medium text-foreground">
              {metodoPagamento === "SDD" ? "Commissione in più (€)" : "Commissione (€)"}{" "}
              <span className="text-destructive">*</span>
            </label>
            <input
              id="regola-valore"
              type="number"
              min="0"
              step="0.01"
              value={valore}
              onChange={(e) => setValore(e.target.value)}
              onBlur={validateValore}
              placeholder={metodoPagamento === "SDD" ? "es. 10.00" : "es. 45.00"}
              className="h-8 w-full min-w-0 rounded-lg border border-input bg-transparent px-2.5 py-1 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 tabular-nums"
            />
            {valoreError && (
              <p className="text-xs text-destructive">{valoreError}</p>
            )}
          </div>

          {/* Metodo pagamento */}
          <div className="space-y-1">
            <label htmlFor="regola-metodo-pagamento" className="text-sm font-medium text-foreground">
              Pagamento
            </label>
            <div className="relative">
              <select
                id="regola-metodo-pagamento"
                value={metodoPagamento}
                onChange={(e) => handleMetodoPagamentoChange(e.target.value)}
                className="h-8 w-full rounded-lg border border-input bg-transparent px-2.5 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 appearance-none pr-7"
              >
                <option value="">qualsiasi</option>
                <option value="SDD">con RID</option>
              </select>
              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
                ▾
              </span>
            </div>
          </div>

          {/* Ricavo in più — solo sulle righe SDD */}
          {metodoPagamento === "SDD" && (
            <div className="space-y-1">
              <label htmlFor="regola-valore-ricavo" className="text-sm font-medium text-foreground">
                Ricavo in più (€)
              </label>
              <input
                id="regola-valore-ricavo"
                type="number"
                min="0"
                step="0.01"
                value={valoreRicavo}
                onChange={(e) => setValoreRicavo(e.target.value)}
                onBlur={validateValoreRicavo}
                placeholder="es. 20.00"
                className="h-8 w-full min-w-0 rounded-lg border border-input bg-transparent px-2.5 py-1 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 tabular-nums"
              />
              {valoreRicavoError && (
                <p className="text-xs text-destructive">{valoreRicavoError}</p>
              )}
            </div>
          )}

          {/* Nome */}
          <div className="space-y-1">
            <label htmlFor="regola-nome" className="text-sm font-medium text-foreground">
              Nome (facoltativo)
            </label>
            <input
              id="regola-nome"
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="se vuoto lo compone il portale"
              className="h-8 w-full min-w-0 rounded-lg border border-input bg-transparent px-2.5 py-1 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50"
            />
          </div>

          {/* Live tier preview */}
          <div
            className={`rounded-lg border px-3 py-2.5 text-sm ${
              tierInvalid
                ? "border-destructive/40 bg-destructive/10 text-destructive"
                : "border-border bg-muted/40 text-foreground"
            }`}
          >
            {tierInvalid ? (
              <p className="font-medium">
                Offerta e fornitore insieme no: l'offerta indica già il
                fornitore. Lasciane uno solo.
              </p>
            ) : (
              <p>
                Riga <strong>{tier}</strong>: {tierLabel(tier)}.
                <br />
                <span className="text-muted-foreground text-xs">
                  {tierScope(tier)}
                </span>
                {metodoPagamento === "SDD" && (
                  <>
                    <br />
                    <span className="text-muted-foreground text-xs">
                      Con RID questo importo si <strong>aggiunge</strong> alla
                      commissione normale, non la sostituisce.
                    </span>
                  </>
                )}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={submitting || tierInvalid}
              className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {submitting ? "Salvataggio..." : isEdit ? "Salva modifiche" : "Crea regola"}
            </button>
          </div>
        </form>

        {/* Inline toast */}
        {toast && (
          <div
            className={`mt-3 px-3 py-2 rounded-lg text-sm ${
              toast.type === "ok"
                ? "bg-green-100 text-green-800 border border-green-200"
                : "bg-red-100 text-red-800 border border-red-200"
            }`}
          >
            {toast.msg}
          </div>
        )}
      </div>
    </div>
  );
}
